import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Box, Plus, Search, Loader2, Trash2, Pencil, Package } from 'lucide-react';
import { toast } from 'sonner';

const MATERIALS = [
  { value: 'plastica', label: 'Plastica' },
  { value: 'cartone',  label: 'Cartone' },
  { value: 'legno',    label: 'Legno' },
  { value: 'metallo',  label: 'Metallo' },
  { value: 'tessuto',  label: 'Tessuto' },
  { value: 'altro',    label: 'Altro' },
];

const initForm = () => ({
  name: '', material_type: 'plastica',
  length_cm: '', width_cm: '', height_cm: '',
  tare_weight_g: '', notes: '',
});

const calcVolume = (l, w, h) => {
  const L = parseFloat(l) || 0, W = parseFloat(w) || 0, H = parseFloat(h) || 0;
  if (!L || !W || !H) return 0;
  return (L * W * H) / 1_000_000;
};

const fmtVol = (m3) => m3 ? Number(m3).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 4 }) : '0';

export default function PackagingCatalog() {
  const { canCreate, canDelete } = usePermissions();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initForm());

  const fetchAll = async () => {
    try {
      const res = await axios.get(`${API}/packaging`);
      setItems(res.data);
    } catch { toast.error('Errore caricamento imballaggi'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const openNew = () => { setEditing(null); setForm(initForm()); setShowForm(true); };
  const openEdit = (it) => {
    setEditing(it._id || it.id);
    setForm({
      name: it.name || '',
      material_type: it.material_type || 'plastica',
      length_cm: it.length_cm ?? '',
      width_cm:  it.width_cm  ?? '',
      height_cm: it.height_cm ?? '',
      tare_weight_g: it.tare_weight_g ?? '',
      notes: it.notes || '',
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Nome imballaggio obbligatorio'); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        material_type: form.material_type,
        length_cm: parseFloat(form.length_cm) || 0,
        width_cm:  parseFloat(form.width_cm)  || 0,
        height_cm: parseFloat(form.height_cm) || 0,
        tare_weight_g: parseFloat(form.tare_weight_g) || 0,
        notes: form.notes,
      };
      if (editing) {
        await axios.put(`${API}/packaging/${editing}`, payload);
        toast.success('Imballaggio aggiornato');
      } else {
        await axios.post(`${API}/packaging`, payload);
        toast.success('Imballaggio creato');
      }
      setShowForm(false);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Eliminare imballaggio "${name}"?`)) return;
    try {
      await axios.delete(`${API}/packaging/${id}`);
      toast.success('Imballaggio eliminato');
      fetchAll();
    } catch { toast.error('Errore'); }
  };

  const filtered = items.filter(it => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (it.name || '').toLowerCase().includes(q)
        || (it.material_type || '').toLowerCase().includes(q)
        || (it.notes || '').toLowerCase().includes(q);
  });

  const previewVolume = calcVolume(form.length_cm, form.width_cm, form.height_cm);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Catalogo Imballaggi</h1>
          <p className="text-muted-foreground text-sm mt-1">{items.length} tipi di imballaggio configurati</p>
        </div>
        {canCreate('orders') && (
          <Button onClick={openNew} data-testid="add-packaging-btn">
            <Plus size={16} className="mr-2" />Nuovo Imballaggio
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Cerca per nome, materiale..."
          className="pl-9 h-9"
          value={search}
          onChange={e => setSearch(e.target.value)}
          data-testid="packaging-search"
        />
      </div>

      {/* Cards / Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16">
            <Box size={48} className="text-muted-foreground mx-auto mb-3" />
            <h3 className="font-semibold text-lg mb-1">Nessun imballaggio</h3>
            <p className="text-sm text-muted-foreground mb-4">Crea il tuo primo tipo di imballaggio per iniziare a tracciare colli e volumi nei DDT.</p>
            {canCreate('orders') && (
              <Button onClick={openNew}><Plus size={14} className="mr-2" />Crea Imballaggio</Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Nome</th>
                  <th className="text-left px-4 py-3 font-medium text-muted-foreground">Materiale</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Lunghezza</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Larghezza</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Altezza</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Volume</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">Tara (g)</th>
                  <th className="text-right px-4 py-3 font-medium text-muted-foreground">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(it => {
                  const id = it._id || it.id;
                  const mat = MATERIALS.find(m => m.value === it.material_type);
                  return (
                    <tr key={id} className="border-b hover:bg-muted/30 transition-colors" data-testid={`packaging-row-${id}`}>
                      <td className="px-4 py-3 font-medium flex items-center gap-2">
                        <Package size={14} className="text-primary" />
                        {it.name}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-muted">{mat?.label || it.material_type}</span>
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground hidden md:table-cell">{it.length_cm} cm</td>
                      <td className="px-4 py-3 text-right text-muted-foreground hidden md:table-cell">{it.width_cm} cm</td>
                      <td className="px-4 py-3 text-right text-muted-foreground hidden md:table-cell">{it.height_cm} cm</td>
                      <td className="px-4 py-3 text-right font-semibold text-primary">{fmtVol(it.volume_m3)} m³</td>
                      <td className="px-4 py-3 text-right text-muted-foreground hidden lg:table-cell">{it.tare_weight_g || 0}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(it)} data-testid={`edit-packaging-${id}`}>
                            <Pencil size={13} />
                          </Button>
                          {canDelete('orders') && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(id, it.name)} data-testid={`delete-packaging-${id}`}>
                              <Trash2 size={13} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
              {editing ? 'Modifica Imballaggio' : 'Nuovo Imballaggio'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5 md:col-span-2">
                <Label>Nome *</Label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Es. Cassetta plastica 60×40×20"
                  data-testid="packaging-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Materiale</Label>
                <Select value={form.material_type} onValueChange={v => setForm(f => ({ ...f, material_type: v }))}>
                  <SelectTrigger data-testid="packaging-material-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MATERIALS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Tara (g)</Label>
                <Input type="number" min="0" step="1" value={form.tare_weight_g}
                  onChange={e => setForm(f => ({ ...f, tare_weight_g: e.target.value }))}
                  placeholder="Peso a vuoto"
                  data-testid="packaging-tare-input" />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5">
                <Label>Lunghezza (cm)</Label>
                <Input type="number" min="0" step="0.1" value={form.length_cm}
                  onChange={e => setForm(f => ({ ...f, length_cm: e.target.value }))}
                  data-testid="packaging-length-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Larghezza (cm)</Label>
                <Input type="number" min="0" step="0.1" value={form.width_cm}
                  onChange={e => setForm(f => ({ ...f, width_cm: e.target.value }))}
                  data-testid="packaging-width-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Altezza (cm)</Label>
                <Input type="number" min="0" step="0.1" value={form.height_cm}
                  onChange={e => setForm(f => ({ ...f, height_cm: e.target.value }))}
                  data-testid="packaging-height-input" />
              </div>
            </div>

            {/* Volume preview */}
            <div className="rounded-md border bg-muted/30 px-4 py-3 flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Volume calcolato</span>
              <span className="text-lg font-bold text-primary" data-testid="packaging-volume-preview">
                {fmtVol(previewVolume)} m³
              </span>
            </div>

            <div className="space-y-1.5">
              <Label>Note</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                rows={2} placeholder="Note opzionali..." />
            </div>

            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setShowForm(false)}>Annulla</Button>
              <Button type="submit" className="flex-1" disabled={saving} data-testid="save-packaging-btn">
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editing ? 'Aggiorna' : 'Crea'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
